import SearchScenarioId from '../store/schema/SearchScenarioId';
import { action } from 'satcheljs';
import {
    SubstrateSearchSuggestionSegment,
    EntityActionTakenType,
    SubstrateSearchScenario,
    ConnectedAccountInfo,
} from 'owa-search-service';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import SuggestionSet, {
    PillSuggestion,
    Suggestion,
} from 'owa-search-service/lib/data/schema/SuggestionSet';
import { addDatapointConfig } from 'owa-analytics-actions';

/**
 * Action dispatched (internally or by consuming client) when a suggestion pill
 * should be added to the search box. "suggestionSelected" should be true for all
 * cases except when user is navigation up/down the SuggestionsContextMenu.
 */
export const addSuggestionPill = action(
    'ADD_SUGGESTION_PILL',
    (
        suggestionPill: PillSuggestion,
        suggestionSelected: boolean,
        scenarioId: SearchScenarioId
    ) => ({
        suggestionPill,
        suggestionSelected,
        scenarioId,
    })
);

/**
 * Action dispatched when the user arrows up or down in the SuggestionsContextMenu,
 * causing the selected index to be changed. This action should be subscribed to
 * by the consuming client (if they're using 3S) to correctly log the
 * "searchboxstatechanged" instrumentation event.
 */
export const changeSelectedSuggestionViaKeyboard = action(
    'CHANGE_SELECTED_SUGGESTION_VIA_KEYBOARD',
    (indexToSelect: number, suggestionToSelect: Suggestion, scenarioId: SearchScenarioId) => ({
        indexToSelect,
        suggestionToSelect,
        scenarioId,
    })
);

// Action dispatched to clear the search input (including query text and pills).
export const clearSearchBox = action('CLEAR_SEARCH_BOX', (scenarioId: SearchScenarioId) => ({
    scenarioId,
}));

// Action dispatched to clear suggestion segments.
export const clearSegments = action('CLEAR_SEGMENTS', (scenarioId: SearchScenarioId) => ({
    scenarioId,
}));

// Action dispatched when a 3S conversation ends.
export const endSearchConversation = action(
    'END_SEARCH_CONVERSATION',
    (scenarioId: SearchScenarioId) => ({
        scenarioId,
    })
);

/**
 * Action dispatched when the user clicks the arrow in command mode to execute
 * a command. Consuming client should set up an orchestrator to subscribe
 * to it and handle what should happen to execute the command.
 */
export const executeCommand = action('EXECUTE_COMMAND', (scenarioId: SearchScenarioId) => ({
    scenarioId,
}));

/**
 * Action dispatched when user exits search mode (i.e. clicking the back arrow
 * in the search box). Consuming client should set up an orchestrator to subscribe
 * to it and handle what should happen in that case (i.e. end search session).
 */
export const exitSearch = action(
    'EXIT_SEARCH',
    (actionSource: string, scenarioId: SearchScenarioId, forceExit: boolean = false) => ({
        actionSource,
        scenarioId,
        forceExit,
    })
);

/**
 * Action dispatched when suggestions should be set in the store. Consuming client
 * should set up an orchestrator to subscribe to it, which should read appropriate
 * values from the search store, fetch suggestions, and set them in the store.
 */
export const getSuggestions = action('GET_SUGGESTIONS', (scenarioId: SearchScenarioId) =>
    addDatapointConfig(
        {
            name: 'Search_SuggestionsE2E',
        },
        {
            scenarioId,
        }
    )
);

/**
 * Action that the consuming client should dispatch when it wants to change the
 * search text. The corresponding orchestrator will set the visible search text,
 * as well as the underlying text used to fetch suggestions and dispatch the
 * "getSuggestions" action so the client knows to fetch new suggestions.
 */
export const onSearchTextChanged = action(
    'ON_SEARCH_TEXT_CHANGED',
    (searchText: string, scenarioId: SearchScenarioId) => ({
        searchText,
        scenarioId,
    })
);

/**
 * Action dispatched when the search box loses focus. Consuming client should
 * set up an orchestrator to subscribe to it and handle what should happen in
 * that case (i.e. end search session).
 */
export const searchBoxBlurred = action('SEARCH_BOX_BLURRED', (scenarioId: SearchScenarioId) => ({
    scenarioId,
}));

/**
 * This action is dispatched after the common search box starts the search session.
 * The consuming client should have an orchestrator that subscribes to it to handle
 * any scenario-specific initialization logic.
 */
export const searchSessionStarted = action(
    'SEARCH_SESSION_STARTED',
    (actionSource: string, shouldStartSearch: boolean, scenarioId: SearchScenarioId) => ({
        actionSource,
        shouldStartSearch,
        scenarioId,
    })
);

/**
 * Action that the consuming client should dispatch when it wants to set suggestions
 * in the store.
 */
export const setCurrentSuggestions = action(
    'SET_CURRENT_SUGGESTIONS',
    (suggestionSet: SuggestionSet, scenarioId: SearchScenarioId, qfRequestId: string = null) => ({
        suggestionSet,
        scenarioId,
        qfRequestId,
    })
);

/**
 * Action that the consuming client should dispatch when it wants to set displayedQFRequestTime
 * in the store (which is the time the request for the currently displayed QF suggestions
 * was sent out).
 */
export const setDisplayedQFRequestTime = action(
    'SET_DISPLAYED_QF_REQUEST_TIME',
    (displayedQFRequestTime: Date, scenarioId: SearchScenarioId) => ({
        displayedQFRequestTime,
        scenarioId,
    })
);

/**
 * Action that the consuming client should dispatch if it wants to set the
 * latest trace ID (QF or query) in the store.
 */
export const setLatestTraceId = action(
    'SET_LATEST_TRACE_ID',
    (latestTraceId: string, scenarioId: SearchScenarioId, isQFTraceId: boolean) => ({
        latestTraceId,
        scenarioId,
        isQFTraceId,
    })
);

/**
 * Action that the consuming client should dispatch if it wants to show/hide
 * the suggestions dropdown.
 */
export const setIsSuggestionsDropdownVisible = action(
    'SET_IS_SUGGESTIONS_DROPDOWN_VISIBLE',
    (isSuggestionsDropdownVisible: boolean, scenarioId: SearchScenarioId) => ({
        isSuggestionsDropdownVisible,
        scenarioId,
    })
);

/**
 * Action that the consuming client should dispatch if it wants to set GF
 * suggestion segments in the store. "suggestionSelected" should be true for all
 * cases except when user is navigation up/down the SuggestionsContextMenu.
 */
export const setSuggestionSegments = action(
    'SET_SUGGESTION_SEGMENTS',
    (
        segments: SubstrateSearchSuggestionSegment[],
        suggestionSelected: boolean,
        scenarioId: SearchScenarioId
    ) => ({
        segments,
        suggestionSelected,
        scenarioId,
    })
);

/**
 * Action dispatched when a search should occur.
 *
 * @param actionSource The source that initiated the search.
 * @param explicitSearch True if from "Enter" on the search box or from search button click; false otherwise.
 */
export const startSearch = action(
    'START_SEARCH',
    (
        actionSource: string,
        scenarioId: SearchScenarioId,
        explicitSearch: boolean = false,
        connectedAccountInfo: ConnectedAccountInfo[] = []
    ) => {
        return {
            actionSource,
            scenarioId,
            explicitSearch,
            connectedAccountInfo,
        };
    }
);

/**
 * This action is dispatched when the user takes an action that should start
 * a search session.
 */
export const startSearchSession = action(
    'START_SEARCH_SESSION',
    (actionSource: string, shouldStartSearch: boolean, scenarioId: SearchScenarioId) => ({
        actionSource,
        shouldStartSearch,
        scenarioId,
    })
);

/**
 * Action dispatched when a suggestion is selected from the SuggestionContextMenu.
 * Consuming client should have an orchestrator that subscribes to it that handles
 * each suggestion type.
 */
export const suggestionSelected = action(
    'SUGGESTION_SELECTED',
    (
        suggestion: Suggestion,
        actionSource: string,
        scenarioId: SearchScenarioId,
        suggestionSetTraceId: string,
        index: number
    ) => ({
        suggestion,
        actionSource,
        scenarioId,
        suggestionSetTraceId,
        index,
    })
);

/**
 * Action dispatched when a secondary action on a suggestion is selected from the
 * SuggestionContextMenu. An example of a secondary action type is the OpenContactCard
 * button on People suggestions.
 * Consuming client should have an orchestrator that subscribes
 * to it that handles each secondary action type.
 */
export const suggestionSecondaryActionSelected = action(
    'SUGGESTION_SECONDARY_ACTION_SELECTED',
    (
        suggestion: Suggestion,
        suggestionIndex: number,
        actionSource: string,
        scenarioId: SearchScenarioId,
        suggestionSetTraceId: string,
        entityActionTakenType: EntityActionTakenType
    ) => ({
        suggestion,
        suggestionIndex,
        actionSource,
        scenarioId,
        suggestionSetTraceId,
        entityActionTakenType,
    })
);

export const feedbackSubmitted = action(
    'FEEDBACK_SUBMITTED',
    (
        scenario: SubstrateSearchScenario,
        yesSelected: boolean,
        suggestionSetTraceId: string,
        feedbackString: string
    ) => ({
        scenario,
        yesSelected,
        suggestionSetTraceId,
        feedbackString,
    })
);

/**
 * This action is dispatched when the user takes an action that should end
 * a search session.
 */
export const endSearchSession = action(
    'END_SEARCH_SESSION',
    (scenarioId: SearchScenarioId, actionSource?: string) => ({
        scenarioId,
        actionSource,
    })
);

/**
 * This action is dispatched after the common search box ends the search session.
 * The consuming client should have an orchestrator that subscribes to it to handle
 * any scenario-specific logic for when a session ends.
 */
export const searchSessionEnded = action(
    'SEARCH_SESSION_ENDED',
    (
        searchSessionGuid: string,
        scenarioId: SearchScenarioId,
        latestTraceId: string,
        actionSource: string
    ) => ({
        searchSessionGuid,
        scenarioId,
        latestTraceId,
        actionSource,
    })
);

/**
 * Updates "IsComplete" property of suggestion set in the store.
 */
export const updateIsSuggestionSetComplete = action(
    'UPDATE_IS_SUGGESTION_SET_COMPLETE',
    (isComplete: boolean, scenarioId: SearchScenarioId) => ({
        isComplete,
        scenarioId,
    })
);

/**
 * Action dispatched when the download button is clicked from a file suggestion.
 * Consuming client should have an orchestrator that subscribes to it to perform the
 * intended functionality of the button.
 */
export const downloadFileSuggestion = action(
    'DOWNLOAD_FILE_SUGGESTION',
    (attachmentId: string, attachmentType: AttachmentType, scenarioId: SearchScenarioId) => ({
        attachmentId,
        attachmentType,
        scenarioId,
    })
);

/**
 * Action dispatched when the share button is clicked from a file suggestion.
 * Consuming client should have an orchestrator that subscribes to it to perform the
 * intended functionality of the button.
 */
export const shareFileSuggestion = action(
    'SHARE_FILE_SUGGESTION',
    (attachmentId: string, attachmentType: AttachmentType, scenarioId: SearchScenarioId) => ({
        attachmentId,
        attachmentType,
        scenarioId,
    })
);

/**
 * Action for the consuming client to register additional quick actions for suggestions
 */
export const registerAdditionalQuickActions = action(
    'REGISTER_ADDITIONAL_QUICK_ACTIONS',
    (scenarioId: SearchScenarioId, callback: (suggestion: Suggestion) => JSX.Element[]) => ({
        scenarioId,
        callback,
    })
);

export const setSearchTextForSuggestion = action(
    'SET_SEARCH_TEXT_FOR_SUGGESTION',
    (searchTextForSuggestion: string, scenarioId: SearchScenarioId) => ({
        searchTextForSuggestion,
        scenarioId,
    })
);
